import { AppLayout } from "@/components/layout/AppLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { useSetupProgress } from "@/hooks/useSetupProgress";
import { CheckCircle2, Circle } from "lucide-react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function SetupProgress() {
  const { steps, percentComplete, allComplete, isLoading } = useSetupProgress();

  return (
    <AppLayout>
      <div className="p-4 md:p-8 space-y-6 max-w-2xl">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Setup Progress</h1>
          <p className="text-muted-foreground mt-1">
            Complete these steps to get your first evaluation.
          </p>
        </div>

        {isLoading ? (
          <Skeleton className="h-64" />
        ) : (
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg">
                  {allComplete ? "You're all set!" : "Getting started"}
                </CardTitle>
                <span className="text-sm font-medium text-muted-foreground">
                  {percentComplete}% to first evaluation
                </span>
              </div>
              <Progress value={percentComplete} className="mt-2 h-2" />
            </CardHeader>
            <CardContent className="space-y-1">
              {steps.map((step) => (
                <Link
                  key={step.key}
                  to={step.href}
                  className={cn(
                    "flex items-center gap-3 rounded-lg px-3 py-3 text-sm transition-colors hover:bg-muted",
                    step.completed
                      ? "text-muted-foreground"
                      : "text-foreground font-medium"
                  )}
                >
                  {step.completed ? (
                    <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground/50 flex-shrink-0" />
                  )}
                  <span className={step.completed ? "line-through" : ""}>
                    {step.label}
                  </span>
                </Link>
              ))}
            </CardContent>
          </Card>
        )}

        {allComplete && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="pt-6">
              <p className="text-sm text-foreground">
                🎉 <span className="font-semibold">First evaluation unlocked!</span>{" "}
                Head to{" "}
                <Link to="/app/orientation/signals" className="text-primary underline underline-offset-2">
                  Basic Signals
                </Link>{" "}
                to see how your hosting is going so far.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </AppLayout>
  );
}
